import React, { useEffect, useReducer, useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import loadDynamicImage from "../../Entities/ImageLoader";
import loadingGif from "../../assets/loading.gif";
import defaultPlant from "../../assets/plants/defaultPlant.jpg";
import PlantDetailsModal from "../Plants/PlantDetailsModal";
import WaterLevelIndicator from '../../Entities/WaterLevelIndicator';
import BatteryLevelIndicator from '../../Entities/BatteryLevelIndicator';
import {
  fetchFullSpacesData,
  fetchUserTimezoneInfo,
} from "../../../api/api_users";
import { update_pot_irrigation_plan } from "../../../api/api_pots";
import {
  ws_pot_irrigate,
  ws_pot_irrigate_stop,
} from "../../../api/api_wsFunctions";

const initialState = {
  spaces: [],
  error: "",
  showInfoPopup: false,
  infoContent: "",
  irrigationSchedules: {},
  customIrrigation: {},
  irrigating: {},
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_SPACES":
      return { ...state, spaces: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "TOGGLE_INFO_POPUP":
      return {
        ...state,
        showInfoPopup: !state.showInfoPopup,
        infoContent: action.payload || state.infoContent,
      };
    case "UPDATE_IRRIGATION_SCHEDULE":
      return {
        ...state,
        irrigationSchedules: {
          ...state.irrigationSchedules,
          [action.potId]: action.schedule,
        },
      };
    case "UPDATE_CUSTOM_IRRIGATION":
      return {
        ...state,
        customIrrigation: {
          ...state.customIrrigation,
          [action.potId]: action.value,
        },
      };
    case "UPDATE_IRRIGATING":
      return {
        ...state,
        irrigating: { ...state.irrigating, [action.potId]: action.value },
      };
    default:
      return state;
  }
}

const irrigationScheduleMap = {
  "-2": "dynamic",
  "-1": "constant",
  0: "none",
  1: "every_day",
  dynamic: "dynamic",
  constant: "constant",
  none: "none",
  every_day: "every_day",
  custom: "custom",
};

const getLightCategory = (value, translations) => {
  if (value <= 10) return { name: translations.no_light || "No Light", num: 1 };
  if (value > 10 && value <= 100)
    return {
      name: translations.low_ambient_light || "Low Ambient Light",
      num: 2,
    };
  if (value > 100 && value <= 500)
    return { name: translations.ambient_light || "Ambient Light", num: 3 };
  if (value > 500 && value <= 3000)
    return {
      name: translations.indirect_solar_light || "Indirect Solar Light",
      num: 4,
    };
  if (value > 3000)
    return {
      name: translations.direct_solar_light || "Direct Solar Light",
      num: 5,
    };
  return { name: "", num: 0 };
};

const getLightCategorybyNum = (value, translations) => {
  if (value <= 1) return translations.no_light || "No Light";
  if (value <= 2 && value > 1)
    return translations.low_ambient_light || "Low Ambient Light";
  if (value <= 3 && value > 2)
    return translations.ambient_light || "Ambient Light";
  if (value <= 4 && value > 3)
    return translations.indirect_solar_light || "Indirect Solar Light";
  if (value <= 5)
    return translations.direct_solar_light || "Direct Solar Light";
  return "";
};

const getStatusIndicator = (current, min, max) => {
  if (
    current === null ||
    current === undefined ||
    min === undefined ||
    max === undefined
  )
    return "";
  if (current >= min && current <= max) {
    return "😃";
  }
  const outOfRange = current < min ? min - current : current - max;
  const range = max - min;
  const excessPercentage = range === 0 ? 100 : (outOfRange / range) * 100;
  if (excessPercentage < 10) {
    return "😐";
  } else if (excessPercentage < 30) {
    return "😟";
  } else {
    return "💀";
  }
};

function getSunlightStatusEmoji(weeklyNum, idealNum) {
  if (weeklyNum == null || idealNum == null) return "—";
  const diff = Math.abs(weeklyNum - idealNum);
  if (diff === 0) return "😃";
  if (diff === 1) return "😐";
  if (diff > 1) return "💀";
  return "—";
}

const formatParameterValue = (parameter, value, translations) => {
  if (value == null || value === undefined) return "—";
  if (parameter === "temperature") {
    return `${parseFloat(value).toFixed(2)} ºC`;
  } else if (parameter === "sunlight_level") {
    const category = getLightCategory(value, translations);
    return `${category.name}`;
  } else if (parameter === "minerals") {
    return `${value} PPM`;
  } else if (parameter === "ground_humidity") {
    return `${value}%`;
  } else {
    return `${value}%`;
  }
};

/**
 * Compute weekly average for sensor measurements.
 * For ground humidity, apply normalization and weighted average of two probes.
 */
function getWeeklyAverage(weekMeasures, param, isGroundHumidity = false) {
  if (!weekMeasures || weekMeasures.length === 0) return null;
  if (isGroundHumidity) {
    let values = weekMeasures
      .map((m) => {
        const moistMain = m.ground_humidity;
        const moistAux = m.ground_humidity_2;
        if (
          moistMain == null ||
          moistAux == null ||
          moistMain < 0 ||
          moistAux < 0
        )
          return null;
        const mainDry = 3150;
        const mainWet = 2150;
        const auxDry = 2550;
        const auxWet = 2250;
        let normalizedMain =
          ((mainDry - moistMain) / (mainDry - mainWet)) * 100;
        let normalizedAux = ((auxDry - moistAux) / (auxDry - auxWet)) * 100;
        normalizedMain = Math.max(0, Math.min(100, normalizedMain));
        normalizedAux = Math.max(0, Math.min(100, normalizedAux));
        const mainWeight = 0.6;
        const auxWeight = 0.4;
        return Number(
          (normalizedMain * mainWeight + normalizedAux * auxWeight).toFixed(1)
        );
      })
      .filter((v) => v !== null);
    if (values.length === 0) return null;
    const sum = values.reduce((a, b) => a + b, 0);
    return Number((sum / values.length).toFixed(1));
  } else {
    let values = weekMeasures
      .map((m) => m[param])
      .filter((v) => v !== undefined && v !== null);
    if (values.length === 0) return null;
    const sum = values.reduce((a, b) => a + b, 0);
    return Number((sum / values.length).toFixed(2));
  }
}

// Non-linear battery percentage using piecewise calibration points.
// Each calibration point: { v: <voltage>, p: <percent 0..100> }.
// The function clamps outside the range and linearly interpolates in-range.
function voltageToPercent(rawValue, calibrationPoints) {
  if (rawValue == null || !Number.isFinite(rawValue)) return null;

  const pts = [...calibrationPoints]
    .filter(p => p && Number.isFinite(p.v) && Number.isFinite(p.p))
    .sort((a, b) => b.v - a.v); // sort high -> low voltage

  if (pts.length < 2) return null;

  const maxV = pts[0].v;
  const minV = pts[pts.length - 1].v;

  // Clamp to the edges
  if (rawValue >= maxV) return Math.round(pts[0].p);
  if (rawValue <= minV) return Math.round(pts[pts.length - 1].p);

  // Find the segment [hi, lo] that contains rawValue
  for (let i = 0; i < pts.length - 1; i++) {
    const hi = pts[i];
    const lo = pts[i + 1];
    if (rawValue <= hi.v && rawValue >= lo.v) {
      const t = (rawValue - lo.v) / (hi.v - lo.v); // 0..1
      const p = lo.p + t * (hi.p - lo.p);
      return Math.round(p);
    }
  }

  return null;
}

// Default calibration tables (tune these with your measured data)
const NON_RECHARGEABLE_CAL = [
  { v: 1420, p: 100 },
  { v: 1300, p: 80 },
  { v: 1200, p: 55 },
  { v: 1150, p: 45 },
  { v: 1100, p: 25 },
  { v: 1070, p: 0 },
];

const RECHARGEABLE_CAL = [
  { v: 1290, p: 100 },
  { v: 1200, p: 75 },
  { v: 1150, p: 60 },
  { v: 1100, p: 40 },
  { v: 1000, p: 15 },
  { v: 930,  p: 0 },
];


/**
 * Format a UTC ISO timestamp directly into a target IANA time zone.
 * Handles DST automatically by delegating to the Intl engine.
 */
function formatUtcWithZone(utcIso, timeZone, locale = navigator.language) {
  if (!utcIso || !timeZone) return null;

  // Ensure we treat naive ISO as UTC by appending 'Z' if no zone info is present
  const iso =
    typeof utcIso === "string" && !utcIso.endsWith("Z") && !utcIso.includes("+")
      ? `${utcIso}Z`
      : utcIso;

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const timeStr = d.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone,
  });
  const dateStr = d.toLocaleDateString(locale, { timeZone });
  return { timeStr, dateStr };
}

const PotManager = () => {
  const { translations } = useLanguage();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [plants, setPlants] = useState({});
  const [customPlants, setCustomPlants] = useState({});
  const [irrigationIntervalId, setIrrigationIntervalId] = useState({});
  const [isPressing, setIsPressing] = useState(false);
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [selectedPlantName, setSelectedPlantName] = useState("");
  const [selectedPlantIsCustom, setSelectedPlantIsCustom] = useState(false);
  const [selectedCustomPlantObj, setSelectedCustomPlantObj] = useState(null);
  const [tzInfo, setTzInfo] = useState({ iana: "UTC", label: "UTC" });
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    async function loadAll() {
      setIsLoading(true);

      // 1) Load timezone info (IANA)
      const tzRes = await fetchUserTimezoneInfo();
      if (mounted && !tzRes.error && tzRes.data) {
        setTzInfo({
          iana: tzRes.data.iana || "UTC",
          label: tzRes.data.label || "UTC",
        });
      }

      // 2) Load spaces & plants data
      const res = await fetchFullSpacesData();
      if (!mounted) return;

      if (res.error) {
        dispatch({ type: "SET_ERROR", payload: translations.fetchDataError });
        setIsLoading(false);
        return;
      }

      dispatch({ type: "SET_SPACES", payload: res.data.spaces });
      setPlants(res.data.plants);
      setCustomPlants(res.data.custom_plants);

      // Initialize irrigation schedule state per pot
      res.data.spaces.forEach((space) => {
        space.hubs.forEach((hub) => {
          hub.pots.forEach((pot) => {
            const timesNum = Number(pot.irrigation_plan_times);
            const irrigationSchedule =
              timesNum >= 2
                ? "custom"
                : irrigationScheduleMap[String(timesNum)] || "none";
            const customIrrigation =
              timesNum >= 2
                ? {
                    days: timesNum,
                    hours: Number(pot.irrigation_plan_hour) || 0,
                  }
                : { days: 0, hours: 0 };
            dispatch({
              type: "UPDATE_IRRIGATION_SCHEDULE",
              potId: pot.id,
              schedule: irrigationSchedule,
            });
            dispatch({
              type: "UPDATE_CUSTOM_IRRIGATION",
              potId: pot.id,
              value: customIrrigation,
            });
          });
        });
      });

      setIsLoading(false);
    }

    loadAll();
    return () => {
      mounted = false;
    };
  }, [translations.fetchDataError]);

  const handleBack = () => navigate("/home");
  const handleDetails = (pot) =>
    navigate(`/pots/${pot.pot_sensor_device_MAC}/details`, { state: { pot } });
  const handleDetailsADMIN = (pot) =>
    navigate(`/pots/${pot.pot_sensor_device_MAC}/adminDetails`, {
      state: { pot },
    });
  const handleEditPot = (pot, irrigation_system_enabled, hubSN) =>
    navigate(`editPot/${pot.pot_sensor_device_MAC}`, {
      state: { hubSN, pot, irrigation_system_enabled },
    });
  const handleAddPlantToPot = (event, pot) => {
    event.stopPropagation();
    navigate(`/plants/addToPot`, { state: { pot } });
  };

  const handleInfo = () => {
    const infoContent = translations.irrigation_info;
    dispatch({ type: "TOGGLE_INFO_POPUP", payload: infoContent });
  };

  const handleIrrigationChange = async (potId, event, potSN) => {
    const token = localStorage.getItem("userToken");
    const value = event.target.value;
    let days = 0;
    let hours = 0;
    switch (value) {
      case "none":
        days = 0;
        hours = 0;
        break;
      case "every_day":
        days = 1;
        hours = 0;
        break;
      case "constant":
        days = -1;
        hours = 0;
        break;
      case "dynamic":
        days = -2;
        hours = 0;
        break;
      case "custom":
        days = state.customIrrigation[potId]?.days || 0;
        hours = state.customIrrigation[potId]?.hours || 0;
        break;
      default:
        days = 0;
        hours = 0;
    }
    dispatch({ type: "UPDATE_IRRIGATION_SCHEDULE", potId, schedule: value });
    if (value === "custom") {
      dispatch({
        type: "UPDATE_CUSTOM_IRRIGATION",
        potId,
        value: { days, hours },
      });
    }
    try {
      const utcHours = (hours - new Date().toLocaleString("en-US", { timeZone: tzInfo.iana, hour: "numeric", hour12: false }) + new Date().getUTCHours()) % 24;
      await update_pot_irrigation_plan(potId, days, utcHours, token, potSN);
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: translations.update_irrigation_days_error,
      });
    }
  };

  const handleCustomIrrigationChange = async (potId, field, event, potSN) => {
    const token = localStorage.getItem("userToken");
    let value = parseInt(event.target.value, 10);
    if (isNaN(value)) value = 0;
    if (field === "days") {
      value = Math.max(0, Math.min(7, value));
    } else if (field === "hours") {
      value = Math.max(0, Math.min(23, value));
    }
    const prev = state.customIrrigation[potId] || { days: 0, hours: 0 };
    const updated = { ...prev, [field]: value };
    dispatch({ type: "UPDATE_CUSTOM_IRRIGATION", potId, value: updated });

    if (state.irrigationSchedules[potId] === "custom") {
      try {
        const utcHours = (updated.hours - new Date().toLocaleString("en-US", { timeZone: tzInfo.iana, hour: "numeric", hour12: false }) + new Date().getUTCHours()) % 24;
        await update_pot_irrigation_plan(
          potId,
          updated.days,
          utcHours,
          token,
          potSN
        );
      } catch (error) {
        dispatch({
          type: "SET_ERROR",
          payload: translations.update_irrigation_days_error,
        });
      }
    }
  };

  const handleStartIrrigation = async (hubSN, potSN, potId) => {
    try {
      await ws_pot_irrigate(hubSN, potSN);
      dispatch({ type: "UPDATE_IRRIGATING", potId, value: true });
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: translations.start_irrigation_error,
      });
    }
  };

  const handleStopIrrigation = async (hubSN, potSN, potId) => {
    try {
      await ws_pot_irrigate_stop(hubSN, potSN);
      dispatch({ type: "UPDATE_IRRIGATING", potId, value: false });
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: translations.stop_irrigation_error,
      });
    }
  };

  const handlePressStart = (hubSN, potSN, potId) => {
    setIsPressing(true);
    handleStartIrrigationInterval(hubSN, potSN, potId);
  };

  const handlePressEnd = (hubSN, potSN, potId) => {
    setIsPressing(false);
    setTimeout(() => {
      handleStopIrrigationInterval(hubSN, potSN, potId);
    }, 500);
  };

  const handlePressCancel = (hubSN, potSN, potId) => {
    if (isPressing) {
      handleStopIrrigationInterval(hubSN, potSN, potId);
      setIsPressing(false);
    }
  };

  const handleStartIrrigationInterval = (hubSN, potSN, potId) => {
    if (irrigationIntervalId[potId]) return;
    handleStartIrrigation(hubSN, potSN, potId);
    const interval = setInterval(() => {
      handleStartIrrigation(hubSN, potSN, potId);
    }, 5000);
    setIrrigationIntervalId((prev) => ({ ...prev, [potId]: interval }));
  };

  const handleStopIrrigationInterval = (hubSN, potSN, potId) => {
    if (irrigationIntervalId[potId]) {
      clearInterval(irrigationIntervalId[potId]);
      setIrrigationIntervalId((prev) => {
        const newState = { ...prev };
        delete newState[potId];
        return newState;
      });
    }
    handleStopIrrigation(hubSN, potSN, potId);
  };

  const getButtonClass = (potID) =>
    state.irrigating[potID]
      ? "general-button secondary-button sm-button irrigating"
      : "general-button secondary-button sm-button not-irrigating";

  if (isLoading) {
    return (
      <div className="pot-loading">
        <img src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  return (
    <>
      {showPlantModal && (
        <PlantDetailsModal
          plantName={
            selectedPlantIsCustom ? selectedPlantName : selectedPlantName
          }
          customPlantObj={selectedPlantIsCustom ? selectedCustomPlantObj : null}
          isCustom={selectedPlantIsCustom}
          onClose={() => setShowPlantModal(false)}
          translations={translations}
        />
      )}
      {state.showInfoPopup && (
        <div className="info-popup">
          <div className="info-popup-content">
            <p dangerouslySetInnerHTML={{ __html: state.infoContent }}></p>
            <button onClick={() => dispatch({ type: "TOGGLE_INFO_POPUP" })}>
              {translations.close}
            </button>
          </div>
        </div>
      )}
      <div className="pot-page">
        <div className="pot-container">
          <div className="header">
            <button onClick={handleBack} className="back-button">
              &#8592;
            </button>
            <h1 className="tittle">
              {translations.my_pots}
              {tzInfo?.label ? (
                <span
                  style={{ marginLeft: 8, fontSize: "0.8em", opacity: 0.7 }}
                >
                  ({tzInfo.label})
                </span>
              ) : null}
            </h1>
          </div>
          {state.error && <p className="general-error">{state.error}</p>}
          {state.spaces.map((space) => (
            <div key={space.id} className="space-container">
              <div className="space-info-big">
                <h4 className="tittle">Space - {space.name}</h4>
                {space.hubs.length === 0 ? (
                  <p>{translations.no_hubs_available}</p>
                ) : (
                  space.hubs.map((hub) => (
                    <div key={hub.id} className="space-info">
                      <h4 className="tittle">
                        {translations.hub} - {hub.name} | 
                        {hub.irrigation_system_enabled && (
                          <>
                            <span className="level-box">
                              <WaterLevelIndicator waterLevel={hub.lastIrrMeasures?.water_level ?? -1} />
                            </span>
                            <span>{`${hub.lastIrrMeasures?.water_level}%`}</span>
                          </>
                        )}
                      </h4>
                      {hub.pots.length === 0 ? (
                        <p>{translations.no_pots_available}</p>
                      ) : (
                        hub.pots.map((pot) => {
                          const plantMetaArr = [];
                          if (pot.plant_ids) {
                            pot.plant_ids
                              .split(",")
                              .map((id) => id.trim())
                              .filter(Boolean)
                              .forEach((pid) => {
                                if (plants[pid]) {
                                  plantMetaArr.push({
                                    name: plants[pid].name,
                                    isCustom: false,
                                  });
                                }
                              });
                          }
                          if (pot.custom_plant_ids) {
                            pot.custom_plant_ids
                              .split(",")
                              .map((id) => id.trim())
                              .filter(Boolean)
                              .forEach((cid) => {
                                if (customPlants[cid]) {
                                  plantMetaArr.push({
                                    name: customPlants[cid].name,
                                    isCustom: true,
                                  });
                                }
                              });
                          }
                          const plantImagesArr = plantMetaArr.map((meta) =>
                            meta.isCustom
                              ? loadDynamicImage("defaultPlant")
                              : loadDynamicImage(meta.name)
                          );

                          return (
                            <div key={pot.id} className="pot-info">
                              <div className="pot-header-flex">
                                <h2 className="pot-header-title">
                                  {pot.name} |
                                  {(() => {
                                    const rawBattery = pot.lastMeasure?.battery_level; // voltage value from sensor

                                    const percentNonRechargeable = voltageToPercent(rawBattery, NON_RECHARGEABLE_CAL);
                                    const percentRechargeable   = voltageToPercent(rawBattery, RECHARGEABLE_CAL);

                                    return (
                                      <div style={{ display: "inline-flex", flexDirection: "column", gap: "4px", marginLeft: 8 }}>
                                        <div>
                                          <span className="level-box">
                                            {/* If BatteryLevelIndicator expects 0..1, divide by 100 */}
                                            <BatteryLevelIndicator batteryLevel={(percentNonRechargeable ?? 0)} />
                                          </span>
                                          <span>
                                            {percentNonRechargeable != null ? `${percentNonRechargeable}% ${(translations.no_rechargeable_battery)}` : "—"}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="level-box">
                                            <BatteryLevelIndicator batteryLevel={(percentRechargeable ?? 0)} />
                                          </span>
                                          <span>
                                            {percentRechargeable != null ? `${percentRechargeable}% ${(translations.rechargeable_battery)}` : "—"}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </h2>
                                <button
                                  onClick={() =>
                                    handleEditPot(
                                      pot,
                                      hub.irrigation_system_enabled,
                                      hub.SN
                                    )
                                  }
                                  className="info-button pot-edit-btn-no-bg"
                                >
                                  <i className="fi fi-rr-pencil"></i>
                                </button>
                              </div>

                              {hub.irrigation_system_enabled && (
                                <div>
                                  <p>{translations.irrigation_plan} - </p>
                                  <select
                                    value={
                                      state.irrigationSchedules[pot.id] ||
                                      "none"
                                    }
                                    onChange={(e) =>
                                      handleIrrigationChange(pot.id, e, pot.SN)
                                    }
                                    className="irrigate-select"
                                  >
                                    <option value="none">
                                      {translations.none}
                                    </option>
                                    <option value="every_day">
                                      {translations.every_day}
                                    </option>
                                    <option value="constant">
                                      {translations.keep_moist}
                                    </option>
                                    <option value="dynamic">
                                      {translations.dynamic}
                                    </option>
                                    <option value="custom">
                                      {translations.custom_days}
                                    </option>
                                  </select>

                                  {state.irrigationSchedules[pot.id] ===
                                    "custom" && (
                                    <div className="irrigation-custom-row">
                                      <input
                                        type="number"
                                        min="0"
                                        max="7"
                                        placeholder={translations.days}
                                        value={
                                          state.customIrrigation[pot.id]
                                            ?.days ?? ""
                                        }
                                        onChange={(e) =>
                                          handleCustomIrrigationChange(
                                            pot.id,
                                            "days",
                                            e,
                                            pot.SN
                                          )
                                        }
                                        className="custom-days-input"
                                      />
                                      <span>{translations.days_per_week}</span>
                                      <input
                                        type="number"
                                        min="0"
                                        max="23"
                                        placeholder={translations.hours}
                                        value={
                                          state.customIrrigation[pot.id]
                                            ?.hours ?? ""
                                        }
                                        onChange={(e) =>
                                          handleCustomIrrigationChange(
                                            pot.id,
                                            "hours",
                                            e,
                                            pot.SN
                                          )
                                        }
                                        className="custom-days-input"
                                      />
                                      <span>{translations.hours_per_day}</span>
                                    </div>
                                  )}
                                  <button
                                    onClick={handleInfo}
                                    className="info-button"
                                  >
                                    &#9432;
                                  </button>
                                </div>
                              )}

                              <div className="pot-header">
                                <button
                                  onClick={() => handleDetails(pot)}
                                  className="general-button fastSettings-button sm-button"
                                >
                                  {translations.graphs}
                                </button>
                                {hub.irrigation_system_enabled && (
                                  <button
                                    className={getButtonClass(pot.id)}
                                    onMouseDown={() =>
                                      handlePressStart(hub.SN, pot.SN, pot.id)
                                    }
                                    onMouseUp={() =>
                                      handlePressEnd(hub.SN, pot.SN, pot.id)
                                    }
                                    onMouseLeave={() =>
                                      handlePressCancel(hub.SN, pot.SN, pot.id)
                                    }
                                    onTouchStart={() =>
                                      handlePressStart(hub.SN, pot.SN, pot.id)
                                    }
                                    onTouchEnd={() =>
                                      handlePressEnd(hub.SN, pot.SN, pot.id)
                                    }
                                    onTouchCancel={() =>
                                      handlePressCancel(hub.SN, pot.SN, pot.id)
                                    }
                                  >
                                    {translations.manual_irrigate}
                                  </button>
                                )}
                              </div>

                              <div className="pot-plants">
                                <p>
                                  {" "}
                                  -{" "}
                                  {plantMetaArr
                                    .map((meta) => meta.name)
                                    .join(" - ")}
                                </p>
                                <div className="plant-add-btn-container">
                                  <button
                                    onClick={(e) => handleAddPlantToPot(e, pot)}
                                    className="general-button sm-button"
                                  >
                                    {translations.add_plants}
                                  </button>
                                </div>
                              </div>

                              <div className="pot-status">
                                {plantImagesArr.map((img, idx) => (
                                  <img
                                    key={idx}
                                    src={img || defaultPlant}
                                    alt="Plant"
                                    className="plant-image pot-inside"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      const meta = plantMetaArr[idx];
                                      if (meta.isCustom) {
                                        setSelectedCustomPlantObj(null);
                                        setSelectedPlantName(meta.name);
                                        setSelectedPlantIsCustom(true);
                                      } else {
                                        setSelectedCustomPlantObj(null);
                                        setSelectedPlantName(meta.name);
                                        setSelectedPlantIsCustom(false);
                                      }
                                      setShowPlantModal(true);
                                    }}
                                  />
                                ))}

                                <div className="scrollable-table">
                                  <table className="status-table">
                                    <thead>
                                      <tr>
                                        <th>{translations.parameter}</th>
                                        <th>{translations.actual_value}</th>
                                        <th>{translations.status}</th>
                                        <th>{translations.ideal_range}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[
                                        "temperature",
                                        "sunlight_level",
                                        "ground_humidity",
                                        "air_humidity",
                                      ].map((param, idx) => {
                                        let avg = null;
                                        if (param === "ground_humidity") {
                                          avg = pot.weekMeasures
                                            ? getWeeklyAverage(
                                                pot.weekMeasures,
                                                param,
                                                true
                                              )
                                            : null;
                                        } else if (param === "sunlight_level") {
                                          avg = pot.weekMeasures
                                            ? getWeeklyAverage(
                                                pot.weekMeasures,
                                                "illumination",
                                                false
                                              )
                                            : null;
                                        } else {
                                          avg = pot.weekMeasures
                                            ? getWeeklyAverage(
                                                pot.weekMeasures,
                                                param,
                                                false
                                              )
                                            : null;
                                        }

                                        let lastValue = null;
                                        if (param === "ground_humidity") {
                                          lastValue = pot.lastMeasure
                                            ? getWeeklyAverage(
                                                [pot.lastMeasure],
                                                param,
                                                true
                                              )
                                            : null;
                                        } else if (param === "sunlight_level") {
                                          lastValue =
                                            pot.lastMeasure?.illumination ??
                                            null;
                                        } else {
                                          lastValue =
                                            pot.lastMeasure?.[param] ?? null;
                                        }

                                        let statusCell = "—";
                                        if (param === "sunlight_level") {
                                          if (
                                            avg !== null &&
                                            pot.sunlight_level !== undefined &&
                                            pot.sunlight_level !== null
                                          ) {
                                            const avgCategoryNum =
                                              getLightCategory(
                                                avg,
                                                translations
                                              ).num;
                                            const idealCategoryNum =
                                              getLightCategory(
                                                Number(pot.sunlight_level),
                                                translations
                                              ).num;
                                            statusCell = getSunlightStatusEmoji(
                                              avgCategoryNum,
                                              idealCategoryNum
                                            );
                                          }
                                        } else if (
                                          param === "ground_humidity"
                                        ) {
                                          if (
                                            lastValue !== null &&
                                            pot[`min_${param}`] !== undefined &&
                                            pot[`min_${param}`] !== null &&
                                            pot[`max_${param}`] !== undefined &&
                                            pot[`max_${param}`] !== null
                                          ) {
                                            statusCell =
                                              getStatusIndicator(
                                                lastValue,
                                                pot[`min_${param}`],
                                                pot[`max_${param}`]
                                              ) || "—";
                                          }
                                        } else {
                                          if (
                                            avg !== null &&
                                            pot[`min_${param}`] !== undefined &&
                                            pot[`min_${param}`] !== null &&
                                            pot[`max_${param}`] !== undefined &&
                                            pot[`max_${param}`] !== null
                                          ) {
                                            statusCell =
                                              getStatusIndicator(
                                                avg,
                                                pot[`min_${param}`],
                                                pot[`max_${param}`]
                                              ) || "—";
                                          }
                                        }

                                        return (
                                          <tr key={idx}>
                                            <td>{translations[param]}</td>
                                            <td>
                                              {lastValue !== null &&
                                              lastValue !== undefined
                                                ? formatParameterValue(
                                                    param,
                                                    lastValue,
                                                    translations
                                                  )
                                                : "—"}
                                            </td>
                                            <td className="status-indicator">
                                              {statusCell}
                                            </td>
                                            <td>
                                              {param === "sunlight_level"
                                                ? pot.sunlight_level !==
                                                    undefined &&
                                                  pot.sunlight_level !== null
                                                  ? getLightCategorybyNum(
                                                      pot.sunlight_level,
                                                      translations
                                                    )
                                                  : "—"
                                                : `${
                                                    pot[`min_${param}`] ?? "—"
                                                  } - ${
                                                    pot[`max_${param}`] ?? "—"
                                                  }`}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              <div className="pot-header">
                                <button
                                  onClick={() => handleDetailsADMIN(pot)}
                                  className="general-button fastSettings-button sm-button hidden-opacity"
                                >
                                  {translations.graphs}
                                </button>
                              </div>

                              <div className="status-row">
                                <div className="status-row-title">
                                  {translations.last_data}
                                </div>
                                <div>
                                  {hub.irrigation_system_enabled && (
                                    <>
                                      {translations.irrigation_system} {" --- "}
                                      {(() => {
                                        const v = hub.lastIrrMeasures?.timestamp
                                          ? formatUtcWithZone(
                                              hub.lastIrrMeasures.timestamp,
                                              tzInfo.iana
                                            )
                                          : null;
                                        return v ? (
                                          <>
                                            {v.timeStr} - {v.dateStr}
                                          </>
                                        ) : (
                                          ""
                                        );
                                      })()}
                                      <br />
                                    </>
                                  )}
                                  {translations.sensor_device} {" --- "}
                                  {(() => {
                                    const v = pot.lastMeasure?.timestamp
                                      ? formatUtcWithZone(
                                          pot.lastMeasure.timestamp,
                                          tzInfo.iana
                                        )
                                      : null;
                                    return v ? (
                                      <>
                                        {v.timeStr} - {v.dateStr}
                                      </>
                                    ) : (
                                      ""
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default React.memo(PotManager);
